import React from 'react'
import compact from 'lodash/compact'
import type { HTMLAttributes } from 'react'
import type { IconType } from '@ztechapps/common.hook'
import { useTheme } from '@ztechapps/common.hook'
import { TooltipPopOver } from '../TooltipPopOver'
import { BootstrapIcon } from './BootstrapIcon'
import { FontAwesomeIcon } from './FontAwesomeIcon'
import { FontAwesome6Icon } from './FontAwesome6Icon'

const EVENT_HANDLER_PROPS = [
  'onClick',
  'onMouseEnter',
  'onMouseLeave',
  'onMouseOver',
  'onMouseOut',
  'onFocus',
  'onBlur',
] as const

export type IconProps = {
  className?: string | undefined
  name?: string
  title?: string
  type?: IconType
  style?: any
  rotation?: number
  // `any`: this one props type spreads onto both `<i>` (HTMLElement) and the FA `<svg>` (SVGSVGElement).
  ref?: any
} & Pick<HTMLAttributes<Element>, 'role' | 'aria-label' | 'aria-hidden' | (typeof EVENT_HANDLER_PROPS)[number]>

export const Icon = ({ title, type, role, 'aria-label': ariaLabel, className, ...props }: IconProps) => {
  const { iconType } = useTheme()
  type = type ?? iconType

  // A titled icon shows its title as hover text; the title also names the glyph unless the caller named it.
  const glyphProps = title
    ? { ...props, role: role ?? 'img', 'aria-label': ariaLabel ?? title }
    : { ...props, role, 'aria-label': ariaLabel }

  // A glyph that hears events — its title's hover, or the handlers a caller's `TooltipPopOver` injects — must be
  // hit-testable. `QueryBuilderInput` loads react-awesome-query-builder's unscoped rule
  // `.svg-inline--fa { pointer-events: none }`, which then covers every SVG glyph on the page, so none would receive
  // them; Bootstrap's `pe-auto` is `!important`, so it wins. A class, not a wrapper: the glyph stays the trigger's
  // own DOM node.
  const needsPointerEvents = !!title || EVENT_HANDLER_PROPS.some((handler) => props[handler])

  //fallback to FontAwesome
  const Glyph = type === 'fa6i' ? FontAwesome6Icon : type === 'bi' ? BootstrapIcon : FontAwesomeIcon

  const glyph = (
    <Glyph {...glyphProps} className={needsPointerEvents ? compact([className, 'pe-auto']).join(' ') : className} />
  )
  return title ? <TooltipPopOver tooltipBody={title}>{glyph}</TooltipPopOver> : glyph
}
